/**
 * cloudinaryHelpers — turn raw Cloudinary URLs into optimized
 * delivery URLs (auto-format + auto-quality, optional width/height).
 *
 * The CMS stores image URLs as plain strings. For Cloudinary-hosted
 * images we want to insert delivery transformations into the URL so
 * the browser receives WebP/AVIF when supported, the right resolution
 * for its viewport, and the smallest acceptable quality.
 *
 * Non-Cloudinary URLs (uploads.org, Airtable attachments, etc.) pass
 * through unchanged — callers can wrap every image URL safely.
 *
 * Cloudinary URL anatomy:
 *   https://res.cloudinary.com/<cloud>/image/upload/<transformations>/<public-id>.<ext>
 *
 * `transformations` is a comma-joined list inserted between
 * `/upload/` and the public_id. Adding more transformations is just
 * a matter of inserting another segment.
 */

const CLOUDINARY_HOST = 'res.cloudinary.com';

/**
 * True when the URL is hosted on a Cloudinary CDN we can transform.
 */
export function isCloudinaryUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    return u.hostname === CLOUDINARY_HOST;
  } catch {
    return false;
  }
}

/**
 * Insert a transformation segment into a Cloudinary URL.
 * Returns the URL unchanged when not a Cloudinary URL OR when the
 * transformation slot can't be located.
 */
function _injectTransform(url, transformStr) {
  if (!transformStr || !isCloudinaryUrl(url)) return url;
  // The slot is /upload/ (image) or /video/upload/ etc. We target image
  // uploads specifically — the others aren't used by the CMS.
  const marker = '/image/upload/';
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  const before = url.slice(0, idx + marker.length);
  const after = url.slice(idx + marker.length);
  // Don't double-stack transformations — if the user passes a URL that
  // already has f_auto/q_auto/etc., just return it unchanged.
  if (/^[a-z]_/i.test(after.split('/')[0] || '')) return url;
  return `${before}${transformStr}/${after}`;
}

/**
 * Wrap a URL with the recommended delivery defaults for the PCC app:
 *   - f_auto: best supported format (WebP / AVIF / JPEG)
 *   - q_auto: dynamic quality based on content
 *   - optionally w_, h_, c_fill for thumbnails
 *
 * For non-Cloudinary URLs, returns the URL as-is so existing external
 * images (Airtable, old Wix uploads, etc.) keep working.
 *
 * @param {string} url
 * @param {object} [opts] — optional overrides
 * @param {number} [opts.width]   — pixel width (Cloudinary appends w_N)
 * @param {number} [opts.height]  — pixel height
 * @param {string} [opts.crop]    — fill | fit | scale | thumb (default: omitted)
 * @param {string} [opts.quality] — auto | 60 | 80 | best (default: auto)
 * @param {string} [opts.format]  — auto | webp | jpg (default: auto)
 * @returns {string}
 */
export function getOptimizedUrl(url, opts = {}) {
  if (!url) return url;
  if (!isCloudinaryUrl(url)) return url;
  const parts = [];
  parts.push(`f_${opts.format || 'auto'}`);
  parts.push(`q_${opts.quality || 'auto'}`);
  if (opts.width) parts.push(`w_${Math.round(opts.width)}`);
  if (opts.height) parts.push(`h_${Math.round(opts.height)}`);
  if (opts.crop) parts.push(`c_${opts.crop}`);
  // DPR-aware delivery for retina screens
  if (opts.dpr !== false) parts.push('dpr_auto');
  return _injectTransform(url, parts.join(','));
}

/**
 * Hero / Banner usage — large above-the-fold image, fill the container.
 * Caps width at 1600 (covers retina 2x of an 800px container) so we
 * don't ship 4K-source images to mobile.
 */
export function getHeroUrl(url, { maxWidth = 1600 } = {}) {
  return getOptimizedUrl(url, { width: maxWidth, crop: 'fill' });
}

/**
 * Card thumbnail — square-ish, smaller payload.
 */
export function getThumbUrl(url, { size = 320 } = {}) {
  return getOptimizedUrl(url, { width: size, height: size, crop: 'fill' });
}

/**
 * Avatar — small round image. Cloudinary's g_face crop hint centers on
 * the detected face if any.
 */
export function getAvatarUrl(url, { size = 96 } = {}) {
  if (!isCloudinaryUrl(url)) return url;
  return _injectTransform(
    url,
    `f_auto,q_auto,w_${size},h_${size},c_fill,g_face,dpr_auto`,
  );
}
